// Single source of truth for the static event content shown on the public
// landing and referenced by the RSVP / admin flows. The DB now owns the
// editable subset of event details (`event_settings` row, singleton id=1),
// bootstrapped with values seeded from `backend/src/db.js`. The seed is
// byte-identical to the values below so the first synchronous render of
// `<InvitationArtwork/>` matches what the async fetch will resolve to
// (no CLS, no flash of empty data — see config-evento-admin design §1).
//
// Field mapping as of v2 (see proposal sdd/config-evento-admin/proposal and
// design §2):
//
//   EDITABLE — moved to `event_settings`, no longer overridden by admins
//   from this file. The values below are SEED-ONLY and must stay in sync
//   with the `INSERT OR IGNORE` block in `backend/src/db.js`:
//     - date.iso              ↔ event_settings.event_date
//     - time.iso              ↔ event_settings.event_time
//     - location.venue        ↔ event_settings.location_venue
//     - location.city         ↔ event_settings.location_city
//     - location.country      ↔ event_settings.location_country
//     - rsvp.deadline.iso     ↔ event_settings.rsvp_deadline (admin-editable
//                              since v2; stored as YYYY-MM-DD, semantics
//                              "until end of that day")
//     (location.maps_url has no EVENT_DATA counterpart; empty by default)
//
//   EDITABLE-IN-DB but NOT exposed in the admin form yet. The values below
//   remain the LIVE source until the admin PUT allow-list is extended:
//     - dressCode.label       (DB: dress_code_label)
//     - dressCode.note        (DB: dress_code_note)
//
//   NEVER EDITABLE — copy-only, owned by this file:
//     - date.humanShort, date.humanLong (Intl date formatting helpers)
//     - time.human, location.display, rsvp.deadline.humanShort/Long
//     - epigraph, blessing, couple, subtitle, bodyParagraph, sideText, closing
//
// When the copy-only fields above change, update ONLY this file — the rest
// of the app reads from here. When the editable-in-DB fields change in
// real life, edit them from `/admin/evento` (frontend) or directly in the
// `event_settings` row in dev. This file's values for those fields
// should be treated as the canonical SEED; do not delete them.
//
// Note: the public landing (`InvitationArtwork`) renders the deadline
// copy from the API value once `useEventSettings` resolves and falls
// back to `rsvp.deadline.humanLong` from this file until then. Once the
// deadline has passed, the artwork hides the "Agradecemos confirmar tu
// asistencia" block entirely (see `frontend/src/lib/rsvpDeadline.js`,
// which mirrors the backend lock semantics).

export const EVENT_DATA = Object.freeze({
  epigraph: 'Que todas nuestras mañanas sean un nosotros',
  blessing: 'Con la bendición de Dios y el amor de nuestras familias,',
  couple: {
    bride: 'María José',
    groom: 'Jean Pier',
    display: 'María José & Jean Pier',
  },
  subtitle: 'Tenemos el gusto de invitarte a celebrar nuestro matrimonio.',
  bodyParagraph:
    'Después de encontrarnos, hemos decidido unir nuestras vidas para siempre. Por eso queremos compartir este momento tan especial con las personas que más queremos.',
  date: {
    iso: '2026-12-05',
    humanShort: 'sábado 5 de diciembre de 2026',
    humanLong: 'sábado 5 de diciembre de 2026',
  },
  time: {
    iso: '16:00',
    human: 'desde las 16h00',
  },
  location: {
    venue: 'Cerro del Carmen',
    city: 'Guayaquil',
    country: 'Ecuador',
    display: 'Cerro del Carmen, Guayaquil, Ecuador',
  },
  dressCode: {
    label: 'Semi Formal',
    note: 'Para ellas: evitar blanco, beige, amarillo, tonos similares y rojo',
  },
  rsvp: {
    deadline: {
      iso: '2026-10-10',
      humanShort: '10 de octubre',
      humanLong: '10 de octubre de 2026',
    },
  },
  sideText:
    'Te esperamos para compartir con nosotros este momento único e inolvidable.',
  closing: 'Con cariño, María José & Jean Pier.',
  // Gift info surfaced at the bottom of the RSVP form so guests who want
  // to send a present have the bank coordinates handy when they confirm.
  // Local-only data — not synced to event_settings.
  gifts: {
    title: 'Regalos',
    intro: 'Si querés tener un detalle con nosotros, puedes hacerlo a través de esta cuenta:',
    bank: 'Banco Pacífico',
    accountType: 'Cuenta de ahorros',
    holder: 'María José González',
    ci: '0930489679',
    cta: '1054177650',
  },
  // Messages shown on the post-deadline SuccessState (when isRsvpLocked
  // has fired). Two variants depending on whether the guest had already
  // responded by the deadline — admin can’t override these; the copy is
  // local to EVENT_DATA so the wording stays in lock-step with the
  // Spanish copy the user signs off on.
  lockedMessages: {
    notResponded:
      'Hola 🤍 Como ya pasó la fecha límite para confirmar la asistencia a nuestra boda y no recibimos respuesta, asumimos que esta vez no podrás acompañarnos. LA LISTA SE HA CERRADO\nNos hubiera encantado tenerte con nosotros, pero será en otra ocasión. 🥂✨ ¡Un abrazo!',
    declined:
      'Gracias por avisarnos que no podrás acompañarnos. Te enviamos un abrazo y esperamos compartir contigo en otra ocasión. 🤍',
    confirmed: 'Te esperamos 🎉🥂✨',
  },
});
